import Head from "next/head";

const SITE_NAME = "SmartTech Academy";
const DEFAULT_DESCRIPTION =
  "SmartTech Academy is a free online school for tech skills. Learn web development, software engineering, and more through structured, project-based courses at no cost.";
const SITE_URL = process.env.NEXT_PUBLIC_APP_URL || "https://smarttechacademy.org";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og/og-default.jpg`;

/**
 * Shared per-page <head> component. Every page should render this with at least a `title`;
 * pages that want real SEO coverage (most of them) should also pass `description`, and
 * `canonicalPath` for pages reachable at more than one URL shape or with query params.
 *
 * `noindex` should be set on private/authenticated pages (dashboard, admin) so search
 * engines never index a logged-in user's personal data.
 */
const PageHead = ({
  title,
  description = DEFAULT_DESCRIPTION,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  canonicalPath,
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = title?.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined;

  return (
    <>
      <Head>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />

        {noindex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}

        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* Open Graph */}
        <meta property="og:type" content={ogType} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        {/* PWA */}
        <meta name="theme-color" content="#2f57ef" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="apple-touch-icon" href="/images/logo/apple-touch-icon.png" />
        {/* iOS Safari has no beforeinstallprompt / manifest-driven install flow -- these are
            the meta tags it actually reads for "Add to Home Screen" to behave like an app
            (standalone chrome, correct status bar, correct home-screen title). */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content={SITE_NAME} />

        {jsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        )}
      </Head>
    </>
  );
};

export default PageHead;
